import { ErrorHandler, Injectable, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { EffectsModule } from '@ngrx/effects';
import { NavigationActionTiming, StoreRouterConnectingModule } from '@ngrx/router-store';
import { CustomSerializer } from './store/states/router.state';
import { LayoutModule } from './layout/layout.module';
import { NgcCookieConsentModule, NgcCookieConsentConfig } from 'ngx-cookieconsent'

import { effects } from './store/effects';
import { reducers } from './store/reducers/app.reducer';
import { TranslateModule } from '@ngx-translate/core';
import { AppConfig } from './app-config.service';
import { HAMMER_GESTURE_CONFIG, HammerGestureConfig, HammerModule } from '@angular/platform-browser';
import { ApiModule } from './api/api.module';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { AuthSessionInterceptor } from './shared/interceptors/auth-session-interceptor.service';
import { hydrationMetaReducer } from './store/reducers/hydratation-meta.reducer';
import { ApiConfiguration } from './api/api-configuration';
import { ToastrModule } from 'ngx-toastr';
import { GlobalErrorHandlerService } from './shared/error-handler/global-error-handler.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { logout } from './store/reducers/logout-meta.reducer';
import { CookieService } from 'ngx-cookie-service';
import { CsrfInterceptorInterceptor } from './shared/interceptors/csrf-interceptor.interceptor';
import { LoaderInterceptorInterceptor} from './shared/interceptors/loader-interceptor.interceptor';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

export function initAppConfiguration(
  appConfig: AppConfig,
  config: ApiConfiguration
): () => Promise<void> {
  return () =>
    appConfig.load().then(() => {
      config.rootUrl = environment.apiUrl;
    });
}

const cookieConsentConfig : NgcCookieConsentConfig = {
  cookie: {
    domain: "dianavet.ch"
  },
  position: "bottom",
  theme: "classic",
  palette: {
    popup: {
      background: "#bd5b02",
      text: "#ffffff",
      link: "#ffffff"
    },
    button: {
      background: "#ffffff",
      text: "#bd5b02",
      border: "transparent"
    }
  },
  type: "info",
  content: {
    message: "This website uses cookies to ensure you get the best experience on our website.",
    dismiss: "Ok",
    deny: "Refuse cookies",
    link: "Learn more",
    href: "https://cookiesandyou.com",
    policy: "Cookie Policy"
  }
}

@Injectable()
export class MyHammerConfig extends HammerGestureConfig {
  overrides = <any>{
    pinch: { enable: false },
    rotate: { enable: false },
    swipe: {
      direction: 6
    }
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    ApiModule.forRoot({ rootUrl: environment.apiUrl }),
    TranslateModule.forRoot({
      defaultLanguage: 'de'
    }),
    StoreModule.forRoot(reducers, {
      metaReducers: environment.hydrateStore ? [hydrationMetaReducer, logout] : [logout],
      runtimeChecks: {
        strictStateImmutability: !environment.production,
        strictActionImmutability: !environment.production,
        strictStateSerializability: false,
        strictActionSerializability: false
      }
    }),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production
    , connectInZone: true}),
    EffectsModule.forRoot(effects),
    StoreRouterConnectingModule.forRoot({
      serializer: CustomSerializer,
      navigationActionTiming: NavigationActionTiming.PostActivation
    }),

    LayoutModule,
    AppRoutingModule,
    HammerModule,
    ToastrModule.forRoot(),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
    NgcCookieConsentModule.forRoot(cookieConsentConfig)
  ],
  providers: [
    provideAppInitializer(() => {
        const initializerFn = (initAppConfiguration)(inject(AppConfig), inject(ApiConfiguration));
        return initializerFn();
      }),
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: MyHammerConfig
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthSessionInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CsrfInterceptorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptorInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic'
      }
    },
    CookieService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
